import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, EmptyState } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload, Download, FileText, X, CheckCircle2, AlertCircle, Clock, Loader2, FileSpreadsheet, Info,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

// Keep in lockstep with bulk_order_service.MAX_ROWS (backend enforces it).
const MAX_ROWS = 2000;
const ACTIVE = ["pending", "processing"];

const STATUS = {
  pending: { cls: "bg-amber-100 text-amber-800", icon: Clock, label: "Pending" },
  processing: { cls: "bg-blue-100 text-blue-800", icon: Loader2, label: "Processing" },
  completed: { cls: "bg-green-100 text-green-800", icon: CheckCircle2, label: "Completed" },
  failed: { cls: "bg-red-100 text-red-800", icon: AlertCircle, label: "Failed" },
  cancelled: { cls: "bg-slate-100 text-slate-700", icon: X, label: "Cancelled" },
};

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const downloadBlob = async (url, filename) => {
  const { data } = await axios.get(url, { responseType: "blob" });
  const href = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
};

const StatusPill = ({ status }) => {
  const cfg = STATUS[status] || STATUS.pending;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} border-0`}>
      <Icon className={`w-3 h-3 mr-1 ${status === "processing" ? "animate-spin" : ""}`} />
      {cfg.label}
    </Badge>
  );
};

const B2CBulkOrders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/b2c/seller/orders/bulk/jobs`, { params: { page: 1, limit: 20 } });
      setJobs(data.jobs || []);
    } catch (e) {
      if (e?.response?.status !== 422) toast.error(errDetail(e, "Failed to load bulk uploads"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Poll active jobs every 3s (single interval; reads latest jobs via a ref).
  useEffect(() => {
    const id = setInterval(async () => {
      const active = jobsRef.current.filter((j) => ACTIVE.includes(j.status));
      if (active.length === 0) return;
      const updates = await Promise.all(
        active.map((j) =>
          axios.get(`${API}/b2c/seller/orders/bulk/jobs/${j.id}`).then((r) => r.data).catch(() => null)
        )
      );
      setJobs((prev) => prev.map((j) => updates.find((u) => u && u.id === j.id) || j));
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const onSelectFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(csv|xlsx)$/i.test(f.name)) {
      toast.error("Please choose a .csv or .xlsx file");
      return;
    }
    setFile(f);
  };

  const onUpload = async () => {
    if (!file) {
      toast.error("Choose a file first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/b2c/seller/orders/bulk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message || "Upload queued");
      setFile(null);
      fetchJobs();
    } catch (e) {
      toast.error(errDetail(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const cancelJob = async (id) => {
    try {
      await axios.post(`${API}/b2c/seller/orders/bulk/jobs/${id}/cancel`);
      toast.success("Job cancelled");
      fetchJobs();
    } catch (e) {
      toast.error(errDetail(e, "Could not cancel job"));
    }
  };

  const num = (v) => v?.job_number || "";

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Bulk Order Upload"
          subtitle="Create many B2C orders at once from a CSV / Excel file"
          backTo={() => navigate("/seller/b2c/orders")}
        />

        {/* Upload card */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-brand" /> Upload a file
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Cap notice — the limit is stated clearly here and enforced server-side. */}
            <div className="flex items-start gap-3 rounded-lg bg-brand-bg/60 border border-brand/20 p-3" data-testid="b2c-bulk-cap">
              <Info className="w-5 h-5 text-brand shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">
                Upload up to <strong className="text-brand">{MAX_ROWS.toLocaleString("en-IN")} orders</strong> per file
                (<strong>one row = one order</strong>). For more, split into several files. Orders import as
                <strong> New</strong> — ship them from B2C Orders (no wallet charge on upload).
              </p>
            </div>

            {/* Template */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg bg-slate-50 p-4">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-8 h-8 text-brand shrink-0" />
                <div>
                  <p className="font-medium text-slate-800">Need the template?</p>
                  <p className="text-sm text-slate-500">Excel template with the columns, hints and instructions.</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() =>
                  downloadBlob(`${API}/b2c/seller/orders/bulk/template`, "b2c_bulk_order_template.xlsx").catch(
                    () => toast.error("Failed to download template")
                  )
                }
                data-testid="b2c-bulk-template"
                className="w-full sm:w-auto"
              >
                <Download className="w-4 h-4 mr-2" /> Download template
              </Button>
            </div>

            {/* File picker */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Select CSV or Excel file</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={onSelectFile}
                  data-testid="b2c-bulk-file"
                  className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-md text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-sm"
                />
                <Button onClick={onUpload} disabled={!file || uploading} data-testid="b2c-bulk-upload" className="w-full sm:w-auto">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload &amp; create
                </Button>
              </div>
              {file && (
                <p className="text-sm text-slate-500">
                  Selected: <span className="font-medium text-slate-700">{file.name}</span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Upload history</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && jobs.length === 0 ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-300" />
              </div>
            ) : jobs.length === 0 ? (
              <EmptyState icon={FileText} title="No bulk uploads yet" description="Download the template, fill in your orders, and upload to create them in one go." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left">
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Job</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">File</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Progress</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Created</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Failed</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Uploaded</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`b2c-bulk-job-${job.id}`}>
                        <td className="px-4 py-3 font-mono text-[13px] text-slate-700 whitespace-nowrap">{num(job)}</td>
                        <td className="px-4 py-3 max-w-[180px] truncate text-slate-700" title={job.file_name}>{job.file_name}</td>
                        <td className="px-4 py-3"><StatusPill status={job.status} /></td>
                        <td className="px-4 py-3">
                          {ACTIVE.includes(job.status) ? (
                            <div className="w-32">
                              <div className="h-2 w-full rounded-full bg-slate-200">
                                <div className="h-2 rounded-full bg-brand transition-all duration-300" style={{ width: `${job.progress_percentage || 0}%` }} />
                              </div>
                              <p className="mt-1 text-xs text-slate-500 tabular-nums">{(job.progress_percentage || 0).toFixed(0)}%</p>
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{job.total_rows}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-green-600">{job.successful_orders}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-red-600">{job.failed_rows}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                          <div>{formatToIST(job.created_at, false)}</div>
                          <div className="text-xs text-slate-400">{formatToIST(job.created_at).split(", ")[1]}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            {job.status === "completed" && job.failed_rows > 0 && (
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                                onClick={() => downloadBlob(`${API}/b2c/seller/orders/bulk/jobs/${job.id}/download-errors`, `b2c_bulk_errors_${num(job)}.csv`).catch(() => toast.error("Download failed"))}
                                title="Download error report" data-testid={`b2c-bulk-errors-${job.id}`}>
                                <AlertCircle className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {job.status === "completed" && (
                              <Button size="sm" variant="outline"
                                onClick={() => downloadBlob(`${API}/b2c/seller/orders/bulk/jobs/${job.id}/download-results`, `b2c_bulk_results_${num(job)}.csv`).catch(() => toast.error("Download failed"))}
                                title="Download results" data-testid={`b2c-bulk-results-${job.id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {ACTIVE.includes(job.status) && (
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                                onClick={() => cancelJob(job.id)} title="Cancel job" data-testid={`b2c-bulk-cancel-${job.id}`}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default B2CBulkOrders;
